import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasAssistedLiving,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBarbecue,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBicycleStorage,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCeilingFan,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDisabledAccess,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDryCleaningDropOff,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasElevator,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGuestSuite,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasHotTub,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOnlineMaintenancePortal,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOnlineRentPayment,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOnsiteManagement,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPackageService,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPetPark,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSauna,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSpanishSpeakingStaff,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasStorageSpace,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSwimmingPool,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTwentyFourHourMaintenance,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasValetTrash,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_isOver55,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_smokingAllowed,
  useRentalManagerUserPropertyEditV2WebApiHandler_isIncomeRestricted,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { amenityPatches } from '../../../../hooks/queries/listingDetailsCache';
import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { useFeedAmenityMutation } from '../../../../hooks/useFeedAmenityMutation';
import { useIsInputFormDisabled } from '../../../../hooks/useIsInputFormDisabled';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';

import {
  PROPERTY_SERVICE_KEYS,
  PROPERTY_SERVICE_METADATA,
  type PropertyServiceKey,
} from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';
import DisabledAccessAmenityTooltip from '../../../DisabledAccessAmenityTooltip';

import { type AmenityFlagSave } from '../../utils';

type LocalState = Record<PropertyServiceKey, boolean>;

const PropertyServicesAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const buildingAttributes = listingDetails?.buildingAttributes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasTwentyFourHourMaintenance: buildingAttributes?.hasTwentyFourHourMaintenance ?? false,
    hasAssistedLiving: buildingAttributes?.hasAssistedLiving ?? false,
    hasBarbecue: buildingAttributes?.hasBarbecue ?? false,
    hasBicycleStorage: buildingAttributes?.hasBicycleStorage ?? false,
    hasCeilingFan: buildingAttributes?.hasCeilingFan ?? false,
    hasDisabledAccess: buildingAttributes?.hasDisabledAccess ?? false,
    hasDryCleaningDropOff: buildingAttributes?.hasDryCleaningDropOff ?? false,
    hasElevator: buildingAttributes?.hasElevator ?? false,
    hasGuestSuite: buildingAttributes?.hasGuestSuite ?? false,
    hasHotTub: buildingAttributes?.hasHotTub ?? false,
    isIncomeRestricted: buildingAttributes?.isIncomeRestricted ?? false,
    hasOnlineMaintenancePortal: buildingAttributes?.hasOnlineMaintenancePortal ?? false,
    hasOnlineRentPayment: buildingAttributes?.hasOnlineRentPayment ?? false,
    hasOnsiteManagement: buildingAttributes?.hasOnsiteManagement ?? false,
    isOver55: buildingAttributes?.isOver55 ?? false,
    hasPackageService: buildingAttributes?.hasPackageService ?? false,
    hasPetPark: buildingAttributes?.hasPetPark ?? false,
    hasSauna: buildingAttributes?.hasSauna ?? false,
    smokingAllowed: buildingAttributes?.smokingAllowed ?? false,
    hasSpanishSpeakingStaff: buildingAttributes?.hasSpanishSpeakingStaff ?? false,
    hasStorageSpace: buildingAttributes?.hasStorageSpace ?? false,
    hasSwimmingPool: buildingAttributes?.hasSwimmingPool ?? false,
    hasValetTrash: buildingAttributes?.hasValetTrash ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Property services updated',
  });

  const isInputFormDisabled = useIsInputFormDisabled(listingDetails, canUpdateListing, FeedOverrideSection.AMENITIES);
  const { isFeedListing, setFeedAmenity } = useFeedAmenityMutation();

  const { mutateAsync: updateTwentyFourHourMaintenance } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTwentyFourHourMaintenance,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTwentyFourHourMaintenance',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateAssistedLiving } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasAssistedLiving,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasAssistedLiving',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateBarbecue } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBarbecue,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: { api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBarbecue', details: { listingAlias } },
    },
  });

  const { mutateAsync: updateBicycleStorage } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBicycleStorage,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBicycleStorage',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateCeilingFan } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCeilingFan,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCeilingFan',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateDisabledAccess } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDisabledAccess,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDisabledAccess',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateDryCleaningDropOff } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDryCleaningDropOff,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDryCleaningDropOff',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateElevator } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasElevator,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: { api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasElevator', details: { listingAlias } },
    },
  });

  const { mutateAsync: updateGuestSuite } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGuestSuite,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGuestSuite',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateHotTub } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasHotTub,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: { api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasHotTub', details: { listingAlias } },
    },
  });

  const { mutateAsync: updateIncomeRestricted } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_isIncomeRestricted,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_isIncomeRestricted', details: { listingAlias } },
    },
  });

  const { mutateAsync: updateOnlineMaintenancePortal } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOnlineMaintenancePortal,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOnlineMaintenancePortal',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateOnlineRentPayment } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOnlineRentPayment,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOnlineRentPayment',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateOnsiteManagement } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOnsiteManagement,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOnsiteManagement',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateOver55 } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_isOver55,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: { api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_isOver55', details: { listingAlias } },
    },
  });

  const { mutateAsync: updatePackageService } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPackageService,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPackageService',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updatePetPark } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPetPark,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: { api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPetPark', details: { listingAlias } },
    },
  });

  const { mutateAsync: updateSauna } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSauna,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: { api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSauna', details: { listingAlias } },
    },
  });

  const { mutateAsync: updateSmokingAllowed } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_smokingAllowed,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_smokingAllowed',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateSpanishSpeakingStaff } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSpanishSpeakingStaff,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSpanishSpeakingStaff',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateStorageSpace } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasStorageSpace,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasStorageSpace',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateSwimmingPool } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSwimmingPool,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSwimmingPool',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateValetTrash } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasValetTrash,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasValetTrash',
        details: { listingAlias },
      },
    },
  });

  const mutationMap = {
    hasTwentyFourHourMaintenance: updateTwentyFourHourMaintenance,
    hasAssistedLiving: updateAssistedLiving,
    hasBarbecue: updateBarbecue,
    hasBicycleStorage: updateBicycleStorage,
    hasCeilingFan: updateCeilingFan,
    hasDisabledAccess: updateDisabledAccess,
    hasDryCleaningDropOff: updateDryCleaningDropOff,
    hasElevator: updateElevator,
    hasGuestSuite: updateGuestSuite,
    hasHotTub: updateHotTub,
    isIncomeRestricted: updateIncomeRestricted,
    hasOnlineMaintenancePortal: updateOnlineMaintenancePortal,
    hasOnlineRentPayment: updateOnlineRentPayment,
    hasOnsiteManagement: updateOnsiteManagement,
    isOver55: updateOver55,
    hasPackageService: updatePackageService,
    hasPetPark: updatePetPark,
    hasSauna: updateSauna,
    smokingAllowed: updateSmokingAllowed,
    hasSpanishSpeakingStaff: updateSpanishSpeakingStaff,
    hasStorageSpace: updateStorageSpace,
    hasSwimmingPool: updateSwimmingPool,
    hasValetTrash: updateValetTrash,
  } as unknown as Record<keyof LocalState, AmenityFlagSave>;

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as PropertyServiceKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = PROPERTY_SERVICE_METADATA[fieldName];
      if (isFeedListing) {
        await setFeedAmenity(metadata.apiKey, checked);
      } else {
        await mutationMap[fieldName]({
          postData: {
            [metadata.apiKey]: checked,
            listingAlias,
          },
        });
      }
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Property services</Legend>} disabled={isInputFormDisabled}>
      {PROPERTY_SERVICE_KEYS.map((fieldName) => {
        const metadata = PROPERTY_SERVICE_METADATA[fieldName];

        return (
          <LabeledControl
            key={`otherTypes-${metadata.label}`}
            label={
              fieldName === 'hasDisabledAccess' ? (
                <>
                  <Label>{metadata.label}</Label>
                  <DisabledAccessAmenityTooltip />
                </>
              ) : (
                <Label>{metadata.label}</Label>
              )
            }
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(PropertyServicesAmenities);
